// ---------------------------------------------------------------------------
// CTA hand-off: carries the user's selections to the Jotform application form
// via BOTH a postMessage to the Wix parent page and a query-param prefill
// link (Jotform prefill: ?uniqueName=value, dates as name[day]/[month]/[year]).
// ---------------------------------------------------------------------------

import {
  APPLICATION_FORM_URL,
  JOTFORM_FIELDS,
  MESSAGE_TYPES,
  PARENT_ORIGIN,
} from "../config";
import type { Band } from "./premiums";

/** Default value for the enquiry form's "Enquiry" dropdown. */
export const DEFAULT_ENQUIRY = "Interested in Gap & Copay Cover";

export interface ApplyPayload {
  planId: string;
  planName: string;
  familySize: 1 | 2;
  /** Human-readable cover type: "Individual" or "Family (2+)". */
  coverType: string;
  age: number;
  band: Band;
  premium: number;
  /** Value to set on the enquiry form's "Enquiry" dropdown. */
  enquiry: string;
  /** Ready-made summary for the "Tell us more about your request" field. */
  summary: string;
  /** Derived date of birth (1 January of currentYear - age), ISO yyyy-mm-dd. */
  dob: string;
  /** Prefilled Jotform URL, ready to open or load into an embedded iframe. */
  prefillUrl: string;
}

/**
 * Derive a date of birth from the entered age: 1 January of
 * (current year - age) — a person born then is exactly `age` all year.
 */
export function dobFromAge(age: number, today: Date = new Date()): Date {
  return new Date(today.getFullYear() - age, 0, 1);
}

/** Assemble the full hand-off payload (incl. summary + prefill URL) for a quote. */
export function buildApplyPayload(input: {
  planId: string;
  planName: string;
  familySize: 1 | 2;
  age: number;
  band: Band;
  premium: number;
}): ApplyPayload {
  // Must match the Jotform "familySize" dropdown options exactly.
  const coverType = input.familySize === 1 ? "Individual" : "Family (2+)";
  const summary = [
    "Gap & CoPay quote request:",
    `• Plan: ${input.planName}`,
    `• Cover: ${coverType}`,
    `• Age of principal insured: ${input.age}`,
    `• Estimated monthly premium: R${input.premium.toLocaleString("en-ZA")}/month`,
  ].join("\n");
  const dobDate = dobFromAge(input.age);
  const dob = `${dobDate.getFullYear()}-01-01`;
  const payload = {
    ...input,
    coverType,
    enquiry: DEFAULT_ENQUIRY,
    summary,
    dob,
  };
  return { ...payload, prefillUrl: buildPrefillUrl(payload) ?? "" };
}

/**
 * Escape a value for Jotform URL prefill. Jotform's decoder converts every
 * literal "+" in a prefill value to a space (even when sent as %2B), and
 * provides "{+}" as the escape sequence for a real plus sign — so
 * "Family (2+)" must be sent as "Family (2{+})" to match the dropdown option.
 */
function jotformValue(value: string): string {
  return value.replace(/\+/g, "{+}");
}

/**
 * Build the Jotform prefill URL, or null if no application URL is configured.
 * Field unique names come from config (JOTFORM_FIELDS). The plan and cover
 * values must match the form's dropdown option labels exactly; the DOB date
 * field is prefilled via its [day]/[month]/[year] sub-params.
 */
export function buildPrefillUrl(
  payload: Omit<ApplyPayload, "prefillUrl">,
): string | null {
  if (!APPLICATION_FORM_URL) return null;
  const url = new URL(APPLICATION_FORM_URL);
  const f = JOTFORM_FIELDS;
  url.searchParams.set(f.plan, jotformValue(payload.planName));
  url.searchParams.set(f.familySize, jotformValue(payload.coverType));
  const dobYear = payload.dob.slice(0, 4);
  url.searchParams.set(`${f.dob}[day]`, "1");
  url.searchParams.set(`${f.dob}[month]`, "1");
  url.searchParams.set(`${f.dob}[year]`, dobYear);
  return url.toString();
}

/**
 * Notify the Wix parent page of the selection so a Velo listener can scroll to
 * and prefill the embedded Jotform. No-op safe when not framed.
 */
export function postApplyMessage(payload: ApplyPayload): void {
  try {
    window.parent?.postMessage(
      { type: MESSAGE_TYPES.apply, payload },
      PARENT_ORIGIN,
    );
  } catch {
    /* cross-origin frame with a locked-down parent — ignore */
  }
}
